import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import MarkdownMessage from '../../components/chat/MarkdownMessage';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { semanticsApi, type SemanticEntityView } from '../../services/semantics-api';
import type { ChatMessage, V2StreamEvent } from '@/types/chat';
import DefinitionView from './DefinitionView';

interface DiscussMessage {
  role: 'user' | 'assistant';
  text: string;
  proposed: boolean;
}

const EDIT_SUGGESTIONS = [
  'Explain what this definition does',
  'Is there a simpler way to express this?',
  'Add a condition to exclude refunds',
];
const NEW_SUGGESTIONS = [
  'Create a gross margin metric',
  'Define a "repeat customers" segment',
  'Add a glossary term for AOV',
];

/**
 * ✦ Discuss with AI for the semantic layer: conversation left, live definition
 * right. The agent drafts changes it SAVES as a proposed version — arriving as a
 * version_proposed event — which the user then Confirms in the pane. Works both
 * on an existing definition and for drafting a brand-new one.
 */
const DiscussView: React.FC<{
  entity: SemanticEntityView | null;
  accountId?: string | null;
  /** Required in new mode — the business the new definition belongs to. */
  organizationId?: string;
  onConfirm: () => void;
  onDiscard: () => void;
  busy: boolean;
  onEntityCreated: (entityId: string) => void;
}> = ({ entity, accountId, organizationId, onConfirm, onDiscard, busy, onEntityCreated }) => {
  const queryClient = useQueryClient();
  const isNew = !entity;
  const [messages, setMessages] = useState<DiscussMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [justProposed, setJustProposed] = useState(false);
  const newThreadId = useRef<string | null>(null);
  const historyLoadedFor = useRef<string | null>(null);
  const msgsRef = useRef<HTMLDivElement>(null);

  const { data: discussState, isLoading } = useQuery({
    queryKey: ['semanticDiscuss', entity?.id],
    queryFn: () => semanticsApi.getDiscuss(entity!.id),
    enabled: !!entity,
  });

  // Hydrate local messages from the persistent thread once per entity.
  useEffect(() => {
    if (!entity || !discussState || historyLoadedFor.current === entity.id) return;
    historyLoadedFor.current = entity.id;
    setMessages(discussState.messages.map(toDiscussMessage).filter((m) => m.text));
  }, [discussState, entity]);

  useEffect(() => {
    msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight });
  }, [messages, working]);

  const send = async (text: string): Promise<void> => {
    const message = text.trim();
    if (!message || streaming) return;
    setInput('');
    setStreaming(true);
    setMessages((prev) => [...prev, { role: 'user', text: message, proposed: false }]);

    // In new mode, defer navigation until the stream resolves — the server
    // adopts the thread to the new entity just before closing, so navigating
    // earlier would race that write and orphan the conversation.
    let createdEntityId: string | null = null;
    let assistantStarted = false;
    const appendDelta = (delta: string): void => {
      setMessages((prev) => {
        const next = [...prev];
        if (!assistantStarted || next[next.length - 1]?.role !== 'assistant') {
          assistantStarted = true;
          next.push({ role: 'assistant', text: delta, proposed: false });
        } else {
          next[next.length - 1] = { ...next[next.length - 1], text: next[next.length - 1].text + delta };
        }
        return next;
      });
    };

    const markProposed = (): void => {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === 'assistant') next[next.length - 1] = { ...last, proposed: true };
        else next.push({ role: 'assistant', text: '', proposed: true });
        return next;
      });
    };

    const onEvent = (event: V2StreamEvent): void => {
      switch (event.type) {
        case 'thread_created':
          newThreadId.current = (event.threadId as string) ?? null;
          break;
        case 'text_delta':
          setWorking(null);
          appendDelta(event.delta as string);
          break;
        case 'tool_start':
          setWorking(
            String(event.toolName).startsWith('preview_')
              ? 'Checking the definition…'
              : event.toolName === 'save_definition_proposal' || event.toolName === 'create_definition_proposal'
                ? 'Saving the proposal…'
                : 'Working…',
          );
          break;
        case 'tool_result':
          setWorking(null);
          break;
        case 'version_proposed': {
          markProposed();
          setJustProposed(true);
          setTimeout(() => setJustProposed(false), 4000);
          const createdId = event.entityId as string;
          if (isNew && createdId) {
            createdEntityId = createdId; // navigate after the stream resolves
          } else if (entity) {
            void queryClient.invalidateQueries({ queryKey: ['semanticEntity', entity.id] });
            void queryClient.invalidateQueries({ queryKey: ['semanticVersions', entity.id] });
            void queryClient.invalidateQueries({ queryKey: ['semanticsOverview'] });
          }
          break;
        }
        case 'error':
          toast.error((event.error as string) || 'Something went wrong');
          break;
        default:
          break;
      }
    };

    try {
      if (entity) {
        await semanticsApi.discuss(entity.id, { message }, onEvent);
      } else if (organizationId) {
        await semanticsApi.discussNew(
          {
            organization_id: organizationId,
            message,
            ...(accountId ? { data_source_account_id: accountId } : {}),
            ...(newThreadId.current ? { threadId: newThreadId.current } : {}),
          },
          onEvent,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reach the AI');
    } finally {
      setStreaming(false);
      setWorking(null);
      if (entity) void queryClient.invalidateQueries({ queryKey: ['semanticDiscuss', entity.id] });
    }

    // Stream resolved (thread adopted server-side) — now switch to the entity.
    if (createdEntityId) onEntityCreated(createdEntityId);
  };

  const suggestions = isNew ? NEW_SUGGESTIONS : EDIT_SUGGESTIONS;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      {/* Chat column */}
      <div className="flex flex-col min-h-[520px] max-h-[70vh] border-b xl:border-b-0 xl:border-r border-border">
        <div ref={msgsRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5">
          {isLoading && entity ? (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : (
            messages.length === 0 &&
            !streaming && (
              <p className="text-[12.5px] text-muted-foreground text-center my-auto px-6">
                {isNew
                  ? 'Describe the metric, segment, time window, glossary term, or product mapping you want — the AI drafts it and saves a proposal you confirm.'
                  : 'Ask the AI to change, simplify, or explain this definition — any change it drafts is saved as a proposal you can confirm or discard.'}
              </p>
            )
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={clsx(
                'max-w-[90%] text-[12.5px] leading-relaxed rounded-xl px-3 py-2',
                m.role === 'user'
                  ? 'self-end bg-primary text-primary-foreground rounded-br-[4px]'
                  : 'self-start bg-muted text-foreground rounded-bl-[4px]',
              )}
            >
              {m.role === 'assistant' ? <MarkdownMessage content={m.text} /> : m.text}
              {m.proposed && (
                <div className="mt-1.5 text-[10.5px] font-bold text-warn">
                  ✦ Proposed — review it on the right, then Confirm
                </div>
              )}
            </div>
          ))}
          {working && (
            <div className="self-start flex items-center gap-2 text-[12px] font-semibold text-primary px-1 py-1">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {working}
            </div>
          )}
          {streaming && !working && messages[messages.length - 1]?.role === 'user' && (
            <div className="self-start flex items-center gap-2 text-[12px] font-semibold text-primary px-1 py-1">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Thinking…
            </div>
          )}
        </div>

        {messages.length === 0 && !streaming && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-2.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void send(s)}
                className="text-[11px] rounded-[9px] bg-muted border border-border px-2.5 py-1 text-foreground hover:bg-accent"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
          className="flex gap-2 px-3.5 py-3 border-t border-border"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isNew ? 'Describe the definition you want…' : 'Ask the AI to change or explain this…'}
            className="flex-1 rounded-[10px] border border-input bg-background px-3 py-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={streaming || !input.trim()}
            className="rounded-[10px] bg-primary text-primary-foreground px-3.5 py-2 text-[12.5px] font-semibold disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>

      {/* Live definition */}
      <div className="min-h-[520px]">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border text-[11.5px] text-muted-foreground">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 text-[9.5px] font-extrabold tracking-wider rounded-full px-2 py-0.5',
              justProposed ? 'text-warn bg-warn/10' : 'text-success bg-success/10',
            )}
          >
            <span className={clsx('w-1.5 h-1.5 rounded-full animate-pulse', justProposed ? 'bg-warn' : 'bg-success')} />
            {justProposed ? 'NEW PROPOSAL' : 'LIVE'}
          </span>
          {justProposed
            ? 'The AI saved a proposal — review it and Confirm to make it live.'
            : 'Changes the AI drafts are saved as proposals you confirm.'}
        </div>
        {entity ? (
          <DefinitionView entity={entity} onConfirm={onConfirm} onDiscard={onDiscard} busy={busy} />
        ) : (
          <div className="px-5 py-10 text-center text-[12.5px] text-muted-foreground">
            Your new definition will appear here once the AI drafts it.
          </div>
        )}
      </div>
    </div>
  );
};

function toDiscussMessage(m: ChatMessage): DiscussMessage {
  const text = m.segments
    .filter((s): s is Extract<typeof s, { type: 'text' }> => s.type === 'text')
    .map((s) => s.text)
    .join('\n\n');
  return { role: m.role === 'user' ? 'user' : 'assistant', text, proposed: false };
}

export default DiscussView;
